import React, { useContext, useEffect, useState } from 'react'
import { TemplatePage } from '../../../components/TemplatePage'
import { FlatList, Text, View } from 'react-native'
import { globalStyle } from '../../../theme/global'
import { Filters } from './components/Filters'
import { Header } from './components/Header'
import { Item } from './components/Item'
import { NecropsyContext } from '../../../context/Necropsy/Provider'
import { DataForm, StateForm } from '../../../context/Necropsy/types'
import { useForm } from '../../../hooks/useForm'
import { useHasInternet } from '../../../hooks/useHasInternet'
import { AlertMessage } from '../../../components/AlertMessage'
import { useNavigation } from '@react-navigation/native'
import { NecropsyRoutes } from '../../../navigator/types'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSendData } from './hooks/useSendData'
import { useCaseSave } from './hooks/useCaseSave'
import { ActionsButtons } from './components/ActionsButtons'
import { Alert401 } from '../../../components/AlertMessage/Alert401'
import { AuthContext } from '../../../context/Auth/AuthContext'
import { useTranslation } from 'react-i18next'
import { useValidation } from './hooks/useValidation'
import { textColor } from '../../../theme/colors'

interface Values {
  allSelected: boolean
  showAlert: boolean
  filterBy: null | StateForm
  sendAlert: string
}
interface NavigationParams extends StackNavigationProp<NecropsyRoutes, 'NecropsyRecords'> {}

export const NecropsyRecords = () => {
  const { handleLogOut } = useContext(AuthContext)
  const { t } = useTranslation()
  const navigation = useNavigation<NavigationParams>()
  const [sendingRecords, setSendingRecords] = useState(false)

  const { getDataByState, deleteRecords, changeStateForm } = useContext(NecropsyContext)
  const [selected, setSelected] = useState<{
    [key: string]: DataForm
  }>({})

  const { isConnected } = useHasInternet()

  const { getValidation } = useValidation({
    onSuccess(data) {
      changeStateForm(
        data.map((id) => ({
          id,
          stateForm: 'sent',
        })),
      )

      setSelected({})
      setSendingRecords(false)
    },
    onError(data) {
      onChangeValues({
        name: 'sendAlert',
        value: 'Caso incompleto. Por favor, envíe el caso nuevamente.' + JSON.stringify(data),
      })
      changeStateForm(
        data.map((id) => ({
          id,
          stateForm: 'pending',
        })),
      )
      setSendingRecords(false)
    },
  })

  const {
    handleSendData: sendRecords,
    isLoading: isLoadingRecord,
    codeError: saveRegisterError,
  } = useSendData({
    onSuccess(data) {
      getValidation(data)
    },
    onError(error, body) {
      onChangeValues({
        name: 'sendAlert',
        value: 'Error 1 enviando los registros: ' + JSON.stringify(error),
      })
      changeStateForm(
        body.map((id) => ({
          id,
          stateForm: 'pending',
        })),
      )
      setSendingRecords(false)
    },
  })

  const {
    handleSendData,
    isLoading: isLoadingCase,
    codeError: saveCaseError,
  } = useCaseSave({
    onSuccess(data) {
      changeStateForm(
        data.map((item) => ({
          id: item.numero_caso,
          stateForm: 'sending',
        })),
      )
      sendRecords(
        data.map((item) => ({
          caseNumber: item.numero_caso,
          caseId: item.id_necropsia_caso,
        })),
      )
    },

    onError(error, body) {
      onChangeValues({
        name: 'sendAlert',
        value: 'Error 2 guardando los registros: ' + JSON.stringify(error),
      })
      setSendingRecords(false)
      changeStateForm(
        body.map((item) => ({
          id: item.numero_caso,
          stateForm: 'pending',
        })),
      )
    },
  })

  const { values, onChangeValues } = useForm<Values>({
    allSelected: false,
    showAlert: false,
    filterBy: null,
    sendAlert: '',
  })

  useEffect(() => {
    const isAllSelected = Object.values(selected).length === getDataByState(values.filterBy).length
    onChangeValues({ name: 'allSelected', value: isAllSelected })
  }, [selected])

  const handleSelectedItem = (item: DataForm) => {
    setSelected((oldSelected) => {
      const { [item.formId!]: deleteRecord, ...rest } = oldSelected
      if (deleteRecord) {
        return {
          ...rest,
        }
      }
      return {
        ...oldSelected,
        [item.formId!]: item,
      }
    })
  }

  const selectAll = () => {
    setSelected(() => {
      if (values.allSelected) {
        return {}
      }
      return Object.fromEntries(
        getDataByState(values.filterBy).map((item) => [[item.formId], item]),
      )
    })
    onChangeValues({ name: 'allSelected', value: !values.allSelected })
  }

  const onDelete = () => {
    const listSelected = Object.values(selected)
    const idsToDelete = listSelected.map((item) => item.formId!)

    const newSelected = listSelected.filter((item) => !idsToDelete.includes(item.formId!))

    setSelected(Object.fromEntries(newSelected.map((item) => [[item.formId], item])))
    deleteRecords(idsToDelete)
  }

  const onSending = () => {
    if (!isConnected) {
      onChangeValues({ name: 'showAlert', value: true })
      return
    }
    const selectItems = Object.values(selected)
    if (selectItems.length === 0) return
    setSendingRecords(true)

    handleSendData(selectItems)
  }

  const handleNavigate = (id: string) => {
    navigation.navigate('Review', { recordId: id })
  }

  return (
    <TemplatePage
      styleContent={{ padding: 0, marginBottom: 0 }}
      header
      title={t('Registros necropsia')}
    >
      <View style={[globalStyle.viewContainer, { padding: 0 }]}>
        <Filters onChange={(value) => onChangeValues({ name: 'filterBy', value })} />
        {getDataByState(values.filterBy).length > 0 && (
          <FlatList
            data={getDataByState(values.filterBy)}
            ListHeaderComponent={() => (
              <Header
                onPress={selectAll}
                isCheck={values.allSelected}
                isSending={isLoadingCase || isLoadingRecord}
              />
            )}
            renderItem={({ item, index }) => (
              <Item
                item={item}
                isCheck={!!selected[(item?.formId ?? '')!]}
                onSelect={handleSelectedItem}
                onNavigate={handleNavigate}
                hasBg={index % 2 === 0}
              />
            )}
          />
        )}

        {Object.values(selected).length > 0 && !sendingRecords && (
          <ActionsButtons isLoading={sendingRecords} onDelete={onDelete} onSend={onSending} />
        )}

        {values.showAlert && (
          <AlertMessage
            title={t('Sin internet')}
            successLabel='ok'
            action={() => onChangeValues({ name: 'showAlert', value: false })}
          >
            <Text style={{ textAlign: 'center', fontSize: 18, color: textColor }}>
              {t('Necesitas estar conectado a internet para poder enviar los formularios')}
            </Text>
          </AlertMessage>
        )}

        {values.sendAlert !== '' && (
          <AlertMessage
            title={t('Advertencia')}
            successLabel='ok'
            action={() => onChangeValues({ name: 'sendAlert', value: '' })}
          >
            <Text style={{ textAlign: 'center', fontSize: 18, color: textColor }}>
              {t(values.sendAlert)}
            </Text>
          </AlertMessage>
        )}

        <Alert401
          isRender={saveCaseError === 401 || saveRegisterError === 401}
          action={handleLogOut}
        />
      </View>
    </TemplatePage>
  )
}
